import { Flex, Input, Text } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import {
  useCreateCustomStatus,
  useUpdateCustomStatus,
} from '~/queries/customStatusQueries'
import { Availability } from '~/types/AgentCustomStatus'
import { showSuccessToast } from '~/utils/toast'

import { useCustomStatusModalStyle } from './componentStyles/CustomStatusModal.style'

//add onCreate callback prop
export const CustomStatusModal = ({
  onCreated,
}: {
  onCreated?: () => void
}) => {
  const labelRef = useRef<HTMLInputElement>(null)
  const descriptionRef = useRef<HTMLInputElement>(null)
  const [labelText, setLabelText] = useState<string>('')
  const [descriptionText, setDescriptionText] = useState<string>('')
  const [availability, setAvailability] = useState<Availability>('Away')
  const {
    customStatusModal: [customStatusModalState, setCustomStatusModalState],
  } = useModalState()

  const { pbxId } = useUserState()

  const { mutate: createCustomStatus, isPending: isCreating } =
    useCreateCustomStatus(() => {
      onCreated?.()
      showSuccessToast({ message: 'Status created successfully' })
    })

  const { mutate: updateCustomStatus, isPending: isUpdating } =
    useUpdateCustomStatus(() => {
      showSuccessToast({ message: 'Status updated successfully' })
    })

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabelText(e.target.value)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescriptionText(e.target.value)
  }

  const handleTypeChange = (value: Availability) => {
    setAvailability(value)
  }

  useEffect(() => {
    console.log('TypeValue changed:', availability)
  }, [availability])

  useEffect(() => {
    setLabelText(customStatusModalState.customStatus?.name || '')
    setDescriptionText(customStatusModalState.customStatus?.description || '')
    setAvailability(customStatusModalState.customStatus?.availability || 'Away')
  }, [customStatusModalState])

  const handleClose = () => {
    setCustomStatusModalState({ open: false })
    setLabelText('')
    setDescriptionText('')
    setAvailability('Away')
  }

  const handleSave = () => {
    if (!pbxId) return
    if (labelText && descriptionText) {
      if (customStatusModalState.customStatus?.id) {
        console.log('Updating custom status')
        updateCustomStatus({
          statusId: customStatusModalState.customStatus.id,
          data: {
            pbx_id: pbxId,
            name: labelText,
            description: descriptionText,
            availability: availability,
          },
        })
      } else {
        console.log('Creating custom status')
        createCustomStatus({
          pbx_id: pbxId,
          name: labelText,
          description: descriptionText,
          availability: availability,
        })
      }
      handleClose()
    }
    if (!labelText) {
      labelRef.current?.focus()
    }
    if (!descriptionText) {
      descriptionRef.current?.focus()
    }
  }

  const styles = useCustomStatusModalStyle()

  return (
    <MangoModal
      isOpen={customStatusModalState.open}
      onClose={handleClose}
      header={
        customStatusModalState.customStatus?.id
          ? 'Edit Custom Status'
          : 'Add Custom Status'
      }
      body={
        <Flex sx={styles.bodyContainer}>
          <Flex sx={styles.inputContainer}>
            <Text sx={styles.inputLabel}>Status Name</Text>
            <Input
              ref={labelRef}
              variant="mango"
              placeholder={'Name'}
              value={labelText}
              onChange={handleLabelChange}
            />
          </Flex>
          <Flex sx={styles.inputContainer}>
            <Text sx={styles.inputLabel}>Type</Text>
            <DropdownSelect
              placeholder="Select Type"
              selectedValue={availability}
              items={[
                {
                  value: 'Away',
                  selectedLabel: 'Away',
                  content: (
                    <MenuListItem
                      leftContent={
                        <Flex
                          sx={{
                            width: '1rem',
                            height: '1rem',
                            borderRadius: '6.25rem',
                            background: 'var(--Red-Red700-P, #ED2435)',
                            marginRight: '0.25rem',
                          }}
                        />
                      }
                      primaryText="Away"
                    />
                  ),
                },
                {
                  value: 'Online',
                  selectedLabel: 'Online',
                  content: (
                    <MenuListItem
                      leftContent={
                        <Flex
                          sx={{
                            width: '1rem',
                            height: '1rem',
                            borderRadius: '6.25rem',
                            background: 'var(--Blue-Blue700-P, #007AFF)',
                            marginRight: '0.25rem',
                          }}
                        />
                      }
                      primaryText="Online"
                    />
                  ),
                },
              ]}
              onSelect={handleTypeChange}
            />
          </Flex>
          <Flex sx={styles.inputContainer}>
            <Text sx={styles.inputLabel}>Description</Text>
            <Input
              ref={descriptionRef}
              variant="mango"
              placeholder={'What is this status used for?'}
              value={descriptionText}
              onChange={handleDescriptionChange}
            />
          </Flex>
        </Flex>
      }
      footer={
        <Flex sx={styles.footerContainer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={handleClose}
            isDisabled={isCreating || isUpdating}
          >
            Cancel
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={handleSave}
            isDisabled={isCreating || isUpdating}
          >
            Save
          </MangoButton>
        </Flex>
      }
    />
  )
}
